"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var jsx_runtime_1 = require("react/jsx-runtime");
var dayjs = require("dayjs");
var react_1 = require("react");
var buttons_1 = require("../buttons");
var Typography_1 = require("../Typography");
var DateItem_1 = require("./DateItem");
var hooks_1 = require("./hooks");
var tools_1 = require("./tools");
var YearPickerContent_1 = require("./YearPickerContent");
var DatePickerContent = function (_a) {
    var months = _a.months, weekDays = _a.weekDays, value = _a.value, selectedValue = _a.selectedValue, onChange = _a.onChange, onMonthYearChange = _a.onMonthYearChange, disableBefore = _a.disableBefore, disableAfter = _a.disableAfter, disableYearsBefore = _a.disableYearsBefore, disableYearsAfter = _a.disableYearsAfter, forwardedRef = _a.forwardedRef, enableDates = _a.enableDates, dataTestId = _a["data-testid"];
    var _b = (0, react_1.useState)((value || new Date()).getMonth()), month = _b[0], setMonth = _b[1]; // zero-indexed
    var _c = (0, react_1.useState)((value || new Date()).getFullYear()), year = _c[0], setYear = _c[1];
    var _d = (0, react_1.useState)(selectedValue), selectedDate = _d[0], setSelectedDate = _d[1];
    var _e = (0, react_1.useState)(false), showYear = _e[0], setShowYear = _e[1];
    var prevButtonRef = (0, react_1.useRef)(null);
    var yearButtonRef = (0, react_1.useRef)(null);
    var nextButtonRef = (0, react_1.useRef)(null);
    var datesRefs = (0, react_1.useRef)([]);
    var yearsRef = (0, react_1.useRef)(null);
    var navButtonRefs = (0, react_1.useRef)(null);
    var dates = (0, hooks_1.useCalendarDates)(month, year);
    var before = (0, react_1.useMemo)(function () { return disableBefore; }, [disableBefore]);
    var after = (0, react_1.useMemo)(function () { return disableAfter; }, [disableAfter]);
    (0, react_1.useEffect)(function () {
        setMonth((value || new Date()).getMonth());
        setYear((value || new Date()).getFullYear());
        setSelectedDate(selectedValue);
    }, [value]);
    (0, react_1.useEffect)(function () {
        var _a, _b;
        if (showYear) {
            (_a = yearsRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = yearButtonRef.current) === null || _b === void 0 ? void 0 : _b.blur();
        }
    }, [showYear]);
    var isClicked = (0, react_1.useCallback)(function (target) {
        var _a, _b;
        return ((_a = navButtonRefs.current) === null || _a === void 0 ? void 0 : _a.contains(target)) ||
            ((_b = yearsRef.current) === null || _b === void 0 ? void 0 : _b.isClicked(target));
    }, []);
    (0, react_1.useImperativeHandle)(forwardedRef, function () { return ({ isClicked: isClicked }); });
    var handleSelectDate = function (date) { return function () {
        setSelectedDate(date);
        onChange === null || onChange === void 0 ? void 0 : onChange(date);
    }; };
    var handlePrevious = function () {
        var data = (0, tools_1.getPrevMonth)(month, year);
        setMonth(data.month);
        setYear(data.year);
        onMonthYearChange === null || onMonthYearChange === void 0 ? void 0 : onMonthYearChange(data.month, data.year);
    };
    var handleNext = function () {
        var data = (0, tools_1.getNextMonth)(month, year);
        setMonth(data.month);
        setYear(data.year);
        onMonthYearChange === null || onMonthYearChange === void 0 ? void 0 : onMonthYearChange(data.month, data.year);
    };
    var handleToggleYear = function () {
        setShowYear(!showYear);
    };
    var handleYearSelect = function (y) {
        setYear(y);
        onMonthYearChange === null || onMonthYearChange === void 0 ? void 0 : onMonthYearChange(undefined, y);
        setShowYear(false);
    };
    var handlePrevButtonKeyDown = function (e) {
        switch (e.key) {
            case 'Enter':
                handlePrevious();
                break;
            case 'ArrowRight':
            case 'ArrowDown': {
                e.preventDefault();
                yearButtonRef.current.focus();
                break;
            }
            case 'Tab': {
                e.preventDefault();
                yearButtonRef.current.focus();
                break;
            }
            default:
                break;
        }
    };
    var handleNextButtonKeyDown = function (e) {
        switch (e.key) {
            case 'Enter':
                handleNext();
                break;
            case 'ArrowLeft':
            case 'ArrowUp': {
                e.preventDefault();
                yearButtonRef.current.focus();
                break;
            }
            case 'ArrowRight':
            case 'ArrowDown': {
                e.preventDefault();
                datesRefs.current[0].focus();
                break;
            }
            case 'Tab':
                e.preventDefault();
                if (e.shiftKey) {
                    yearButtonRef.current.focus();
                }
                else {
                    datesRefs.current[0].focus();
                }
                break;
            default:
                break;
        }
    };
    var handleYearButtonKeyDown = function (e) {
        var _a, _b;
        switch (e.code) {
            case 'Enter':
                e.preventDefault();
                handleToggleYear();
                break;
            case 'ArrowLeft':
            case 'ArrowUp': {
                e.preventDefault();
                prevButtonRef.current.focus();
                break;
            }
            case 'ArrowRight':
            case 'ArrowDown': {
                e.preventDefault();
                if (!showYear) {
                    nextButtonRef.current.focus();
                }
                else {
                    yearsRef.current.focus();
                    (_a = yearButtonRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                }
                break;
            }
            case 'Tab':
                e.preventDefault();
                if (e.shiftKey) {
                    prevButtonRef.current.focus();
                }
                else {
                    if (!showYear) {
                        nextButtonRef.current.focus();
                    }
                    else {
                        yearsRef.current.focus();
                        (_b = yearButtonRef.current) === null || _b === void 0 ? void 0 : _b.blur();
                    }
                }
                break;
            default:
                break;
        }
    };
    var handleDateItemKeyDown = function (date, index) { return function (e) {
        var lastIndex = datesRefs.current.length - 1;
        switch (e.key) {
            case 'Enter':
                handleSelectDate(date)();
                break;
            case 'ArrowLeft':
            case 'ArrowUp': {
                e.preventDefault();
                if (index === 0) {
                    nextButtonRef.current.focus();
                }
                else {
                    datesRefs.current[index - 1].focus();
                }
                break;
            }
            case 'ArrowRight':
            case 'ArrowDown': {
                e.preventDefault();
                var nextIndex = index < lastIndex ? index + 1 : 0;
                datesRefs.current[nextIndex].focus();
                break;
            }
            case 'Tab':
                if (e.shiftKey) {
                    if (index === 0) {
                        e.preventDefault();
                        datesRefs.current[lastIndex].focus();
                    }
                }
                else {
                    if (index === lastIndex) {
                        e.preventDefault();
                        datesRefs.current[0].focus();
                    }
                }
                break;
            default:
                break;
        }
    }; };
    return ((0, jsx_runtime_1.jsxs)("div", __assign({ className: "squid-flex squid-flex-col" }, { children: [(0, jsx_runtime_1.jsxs)("div", __assign({ className: "squid-flex squid-items-center squid-justify-center squid-gap-4", ref: navButtonRefs }, { children: [(0, jsx_runtime_1.jsx)(buttons_1.IconButton, { icon: "ChevronLeft", size: "sm", variant: "plain", "aria-label": "Previous", onClick: handlePrevious, onKeyDown: handlePrevButtonKeyDown, disabled: showYear, tabIndex: 0, forwardedRef: prevButtonRef }), (0, jsx_runtime_1.jsx)(buttons_1.SimpleButton, { "aria-label": "".concat(months[month], " ").concat(year), title: "".concat(months[month], " ").concat(year), onClick: handleToggleYear, onKeyDown: handleYearButtonKeyDown, active: showYear, tabIndex: 0, forwardedRef: yearButtonRef }), (0, jsx_runtime_1.jsx)(buttons_1.IconButton, { icon: "ChevronRight", "aria-label": "Next", size: "sm", variant: "plain", onClick: handleNext, onKeyDown: handleNextButtonKeyDown, disabled: showYear, tabIndex: 0, forwardedRef: nextButtonRef })] })), (0, jsx_runtime_1.jsxs)("div", __assign({ className: "squid-flex squid-items-center squid-justify-center" }, { children: [(0, jsx_runtime_1.jsx)(YearPickerContent_1.default, { show: showYear, value: (selectedValue || new Date()).getFullYear(), onChange: handleYearSelect, forwardedRef: yearsRef, disableYearsBefore: disableYearsBefore, disableYearsAfter: disableYearsAfter }), !showYear && ((0, jsx_runtime_1.jsxs)("div", __assign({ className: "squid-grid squid-grid-cols-7 squid-grid-rows-6 squid-w-full squid-gap-2" }, { children: [weekDays.map(function (weekDay) {
                                return ((0, jsx_runtime_1.jsx)("div", __assign({ className: "squid-w-[40px] squid-h-[40px] squid-flex squid-justify-center squid-items-center" }, { children: (0, jsx_runtime_1.jsx)(Typography_1.Label, __assign({ strong: true, className: "squid-text-alias-color-text-weakest squid-text-center" }, { children: weekDay })) }), weekDay));
                            }), dates.prevMonthDates.map(function (day) {
                                return (0, jsx_runtime_1.jsx)(DateItem_1.DateItem, { date: day, disabled: true }, day.toISOString());
                            }), dates.curMonthDates.map(function (day, index) {
                                var disabled = (!!before && dayjs(day).isBefore(before)) ||
                                    (!!after && dayjs(day).isAfter(after)) ||
                                    (!!enableDates && !enableDates.has(day.toISOString()));
                                return ((0, jsx_runtime_1.jsx)(DateItem_1.DateItem, { onClick: !disabled ? handleSelectDate(day) : undefined, onKeyDown: !disabled ? handleDateItemKeyDown(day, index) : undefined, date: day, current: dayjs().isSame(day, 'day'), active: dayjs(selectedDate).isSame(day, 'day'), disabled: disabled, forwardedRef: function (ref) {
                                        if (!disabled) {
                                            datesRefs.current[index] = ref;
                                        }
                                    }, "data-testid": dataTestId }, day.toISOString()));
                            })] })))] }))] })));
};
exports.default = DatePickerContent;
